import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, combineLatest, of } from 'rxjs';
import { ParkingLotDetail } from '@core/models/parking-detail.model';
import { ParkingService } from '../services/parking.service';
import { ParkingLotSlotsResponse } from '@core/models/parking-slot.model';
import { MenuService } from '@core/services/menu.service';
import { SPOT_TYPE_LABEL } from '@shared/constant/shared.constant';
import { BreadcrumbItem, CompanyService } from '@shared/services/company.service';
import { autoUnsubWith } from '@shared/services/utility.abstract';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';

@Component({
  selector: 'app-view-parking',
  templateUrl: './view-parking.component.html',
  styleUrls: ['./view-parking.component.scss']
})
export class ViewParkingComponent implements OnInit{
  readonly destroyRef = inject(DestroyRef);
  spotTypeMsg: string = SPOT_TYPE_LABEL;
  id = "";
  parkingLotData: ParkingLotDetail;
  parkingLotSlotsOutput: any = [];
  editParkingCheck: boolean = false;
  groupedRates = [];
  role : string;
  companyId : string;

  constructor(
    private readonly parkingService: ParkingService,
    private readonly route: ActivatedRoute,
    private readonly menuService: MenuService,
    private readonly router: Router,
    private readonly companyService: CompanyService,
    private readonly encryptStorage: EncryptionStorageService) { }

  ngOnInit() {
    this.role = this.encryptStorage.getItem('roleName'); 
    this.companyId = this.encryptStorage.getItem('companyId');
    this.editParkingCheck = this.menuService.shortNameExists(JSON.parse(this.encryptStorage.getItem('menuData')), 71)
    
    this.getParkingLotId();

    const breadcrumbObj: BreadcrumbItem[] = [
      {
        title: 'Parking Locations',
        url: `super-admin/parking-list/${this.companyId}`
      },
      {
        title: 'View Parking'
      }
    ];
    this.companyService.setAdminHeaders('0', breadcrumbObj);
  }

getParkingLotId(): void {
    this.route.params.subscribe((params) => {
      this.id = params['id'];
      this.getAllParkingLotData();
    });
  }
  getAllParkingLotData(): void {
    
    combineLatest([
      this.parkingService.getParkingLotsById(this.id)
        .pipe(
          catchError(() => {
            return of(null)
          }),
        ),
      this.parkingService.getParkingLotDetailsWithSlots(this.id)
        .pipe(
          catchError(() => {
            return of(null)
          }),
        )
    ])
      .pipe(autoUnsubWith(this.destroyRef)).subscribe({
        next: ([_parkingLot, _parkingLotDetail]: [ParkingLotDetail, ParkingLotSlotsResponse]) => {
          if (_parkingLot) {
            this.parkingLotData = _parkingLot;
            this.groupRatesByType();
          }
          if (_parkingLotDetail) {
            this.parkingLotSlotsOutput = _parkingLotDetail.parkingLotSlotsOutput;
          }
        }
      })
  }

  getPlainText(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}
  
editParkingNavigation(): void {
    let routePath = '';

    switch (this.role) {
      case 'SuperAdmin':
        routePath = 'super-admin/parking/edit';
        break;
      case 'ParkingManager':
        routePath = 'manager/parking/edit';
        break;
      case 'ParkingOwner':
        routePath = 'manager/parking/edit';
        break;
      default:
        return; // Prevent navigation for unauthorized users
    }

    this.router.navigate([routePath, this.id]);
  }
  
  hasRates(specialRates) {
    return specialRates.rates && specialRates.rates.length > 0;
  }

   groupRatesByType() {
    this.groupedRates = this.parkingLotData?.spotTypeSpecialRate.reduce((acc, current) => {
      const existingGroup = acc.find(group => group.spotTypeName === current.spotTypeName);
      if (existingGroup) {
        existingGroup.rates.push(current);
      } else {
        acc.push({
          spotTypeName: current.spotTypeName,
          publicFareChargesDaily: current.publicFareChargesDaily,
          publicFareChargesMonthly: current.publicFareChargesMonthly,
          description: current.description,
          rates: [current]
        });
      }
      return acc;
    }, []);
  }
}
